import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { of } from 'rxjs';
import { Currency } from '@dlap-ui/shared-forms/models/currency';
import { PAYMENTS_CONFIG } from '../../payments.config';
import { CheckoutService } from '../../services/checkout.service';
import { EFeeType, PaymentMethod } from '../../models/payment.enums';
import {
  FeeTypeEntry,
  PaymentConfig,
  PaymentResult,
  SummaryData,
} from '../../models/payment.models';
import { Summary } from '../summary/summary';
import { Mastercard } from '../mastercard/mastercard';
import { EcocashComponent } from '../ecocash/ecocash';
import { Zimswitch } from '../zimswitch/zimswitch';

/** A fee entry enriched with the currency id, used by the currency picker. */
interface CurrencyOption extends FeeTypeEntry {
  currencyId?: string | null;
}

const DEFAULT_CURRENCY: Currency = { symbol: 'USD', name: 'USD' };

/**
 * The "choose a payment method" checkout dialog.
 *
 * A modal counterpart to the full-page {@link Payments} checkout: it drops the
 * numbered sections and the guest personal-information step and shows the
 * amount, a currency switcher and the three collapsible payment methods.
 * Everything is driven by inputs instead of route params, so it can be opened
 * from anywhere (wallet top-up, service fees, …).
 */
@Component({
  selector: 'lib-payment-checkout-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzIconModule,
    NzModalModule,
    NzSelectModule,
    NzSpinModule,
    Summary,
    Mastercard,
    EcocashComponent,
    Zimswitch,
  ],
  templateUrl: './checkout-modal.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckoutModal {
  readonly visible = input(false);
  readonly title = input('Choose a payment method');
  /** Fee type to pay for. Defaults to a wallet top-up (manual amount entry). */
  readonly feeType = input<string | null>(null);
  /** Pay into another user's wallet (admin flow) instead of the signed-in one. */
  readonly userId = input<string | null>(null);
  /** Where the gateway should send the payer back to. */
  readonly returnUrl = input<string | null>(null);

  readonly cancelled = output<void>();
  readonly paid = output<PaymentResult>();

  private readonly checkoutService = inject(CheckoutService);
  private readonly config = inject(PAYMENTS_CONFIG);
  private readonly message = inject(NzMessageService);

  readonly isDeeds = this.config.appName === 'Deeds';

  private readonly storedUser = (() => {
    const raw = localStorage.getItem(this.config.userStorageKey ?? 'auth_user');
    return raw ? JSON.parse(raw) : null;
  })();

  readonly user = computed(() =>
    this.userId() ? { id: this.userId() } : this.storedUser,
  );

  readonly selectedPaymentSection = signal<PaymentMethod>(null);
  readonly selectedCurrencyId = signal<string | null>(null);
  readonly paymentConfig = signal<PaymentConfig | null>(null);
  private readonly manualAmount = signal(0);

  private readonly feeTypesResource = rxResource({
    params: () => (this.visible() ? (this.feeType() ?? EFeeType.WALLET_FEE) : undefined),
    stream: ({ params }) =>
      params ? this.checkoutService.getPublicActiveFeeTypes() : of(null),
  });

  /** Deeds reads the CBZ iVeri application id from the backend. */
  private readonly applicationIdResource = rxResource({
    params: () => (this.visible() && this.isDeeds ? true : undefined),
    stream: ({ params }) => (params ? this.checkoutService.getApplicationId() : of(null)),
  });

  readonly isLoading = computed(
    () => this.feeTypesResource.isLoading() || this.applicationIdResource.isLoading(),
  );

  private readonly feeTypes = computed<FeeTypeEntry[]>(
    () => this.feeTypesResource.value() ?? [],
  );

  /** The fee entry matching the requested fee type, falling back to the wallet fee. */
  private readonly requestedFee = computed<FeeTypeEntry | undefined>(() => {
    const results = this.feeTypes();
    const wanted = this.feeType();

    if (!wanted) {
      return results.find((el) => el.feeType === EFeeType.WALLET_FEE);
    }

    return (
      results.find((el) => el.feeType !== 'LOAD-WALLET' && el.feeType === wanted) ??
      results.find((el) => el.feeType === EFeeType.WALLET_FEE)
    );
  });

  /** The same fee expressed in every currency it is priced in. */
  readonly availableCurrencies = computed<CurrencyOption[]>(() => {
    const fee = this.requestedFee();
    if (!fee) return [];

    return this.feeTypes()
      .filter((entry) => entry.feeType === fee.feeType)
      .map((entry) => ({ ...entry, currencyId: entry.currency?.id }));
  });

  /** The fee actually being charged — the requested one, or the picked currency. */
  readonly activeFee = computed<FeeTypeEntry | undefined>(() => {
    const currencyId = this.selectedCurrencyId();
    if (!currencyId) return this.requestedFee();

    return (
      this.availableCurrencies().find((entry) => entry.currencyId === currencyId) ??
      this.requestedFee()
    );
  });

  /** Wallet top-ups let the payer type the amount; other fees are fixed. */
  readonly isManualInput = computed(
    () => this.activeFee()?.feeType === EFeeType.WALLET_FEE,
  );

  readonly amount = computed(() =>
    this.isManualInput() ? this.manualAmount() : (this.activeFee()?.price ?? 0),
  );

  readonly summaryData = computed<SummaryData>(() => {
    const fee = this.activeFee();
    const amount = this.amount();

    return {
      serviceType: fee?.feeType ?? null,
      serviceName: this.isManualInput() ? 'Top up amount' : (fee?.name ?? null),
      amount,
      total: amount,
      currency: fee?.currency ?? DEFAULT_CURRENCY,
      tax: 0,
    };
  });

  readonly currencyLabel = computed(() =>
    this.currencyCode(this.summaryData().currency),
  );

  constructor() {
    // Start each time the dialog opens from a clean slate.
    effect(() => {
      if (!this.visible()) {
        this.selectedPaymentSection.set(null);
        this.selectedCurrencyId.set(null);
        this.paymentConfig.set(null);
        this.manualAmount.set(0);
      }
    });
  }

  currencyCode(currency: Currency | null | undefined): string {
    return currency?.symbol || currency?.name || 'USD';
  }

  private get callbackUrl(): string {
    return (
      this.returnUrl() ?? this.config.frontendUrl ?? window.location.origin + '/'
    );
  }

  private get applicationId(): string {
    if (!this.isDeeds) return this.config.iveriApplicationId;
    return this.applicationIdResource.value() ?? '';
  }

  selectCurrency(currencyId: string): void {
    this.selectedCurrencyId.set(currencyId);

    // Keep an already-open method section in sync with the new currency.
    const open = this.selectedPaymentSection();
    if (open) {
      this.selectedPaymentSection.set(null);
      this.togglePaymentOption(open);
    }
  }

  onPriceChange(amount: number): void {
    this.manualAmount.set(amount);

    const current = this.paymentConfig();
    if (current) {
      this.paymentConfig.set({ ...current, Amount: amount, amount });
    }
  }

  togglePaymentOption(name: PaymentMethod): void {
    if (this.amount() <= 0) {
      this.message.error('Enter amount first!');
      return;
    }

    if (this.selectedPaymentSection() === name) {
      this.selectedPaymentSection.set(null);
      return;
    }

    this.selectedPaymentSection.set(name);
    this.paymentConfig.set(this.buildPaymentConfig(name));
  }

  private buildPaymentConfig(name: PaymentMethod): PaymentConfig | null {
    const fee = this.activeFee();
    const amount = this.amount();
    const base: PaymentConfig = {
      feeId: fee?.id,
      isWallet: this.isManualInput(),
      callbackUrl: this.callbackUrl,
    };

    if (name === 'MASTERCARD' || name === 'ECOCASH') {
      return {
        ...base,
        gateway: this.config.iveriGateway,
        ReturnUrl: this.config.iveriReturnUrl,
        ApplicationID: this.applicationId,
        Amount: amount,
        Currency: this.currencyCode(fee?.currency),
        PAN: '',
        ExpiryDate: '',
        paymentChannel:
          name === 'ECOCASH' ? 'ECOCASH' : this.isDeeds ? 'CBZ' : 'IVERI',
      };
    }

    if (name === 'ZIMSWITCH') {
      return {
        ...base,
        userId: this.user()?.id,
        amount,
        Amount: amount,
        Currency: this.currencyCode(fee?.currency),
        paymentChannel: this.isDeeds ? 'ZIM_SWITCH' : 'ZIMSWITCH',
      };
    }

    return null;
  }

  onPaymentSubmitted(result: PaymentResult): void {
    this.paid.emit(result);
  }

  cancel(): void {
    this.cancelled.emit();
  }
}
